import type { Chapter, Marker, PartAnalysis, PreviewIndex } from "../sources/types";

/**
 * Pure helpers over GET /v1/parts/{id}/analysis: preview-sheet arithmetic
 * for the scrubber, chapter lookups for the seek bar and menus, marker
 * logic for Skip Intro / Skip Credits. No React, no DOM — the SeekBar,
 * the skip pill and the item page strip all lean on these, and the unit
 * tests pin the arithmetic the server documents on the route.
 */

export const EMPTY_ANALYSIS: PartAnalysis = { chapters: [], markers: [], previews: null };

/** Wire → normalized: missing arrays tolerated, lists sorted by start,
 * an empty preview set collapses to null. */
export function normalizeAnalysis(raw: Partial<PartAnalysis> | null | undefined): PartAnalysis {
  if (!raw) return EMPTY_ANALYSIS;
  const chapters = [...(raw.chapters ?? [])].sort((a, b) => a.startMs - b.startMs);
  const markers = [...(raw.markers ?? [])].sort((a, b) => a.startMs - b.startMs);
  const p = raw.previews;
  const previews =
    p && p.count > 0 && p.intervalMs > 0 && p.width > 0 && p.height > 0 && p.tileCols > 0 && p.tileRows > 0
      ? p
      : null;
  return { chapters, markers, previews };
}

// ---- previews ----

/** The thumbnail index for a time, clamped into [0, count). */
export function previewIndexAt(p: PreviewIndex, timeS: number): number {
  const k = Math.floor((Math.max(0, timeS) * 1000) / p.intervalMs);
  return Math.min(p.count - 1, Math.max(0, k));
}

export interface PreviewCell {
  /** The thumbnail index. */
  index: number;
  /** The sheet (tile) it lives on. */
  tile: number;
  /** Pixel offset of the cell inside the sheet. */
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where the thumbnail for a time sits: sheet and pixel cell (row-major). */
export function previewCellFor(p: PreviewIndex, timeS: number): PreviewCell | null {
  if (p.count <= 0 || p.intervalMs <= 0 || p.tileCols <= 0 || p.tileRows <= 0) return null;
  const index = previewIndexAt(p, timeS);
  const per = p.tileCols * p.tileRows;
  const tile = Math.floor(index / per);
  const cell = index % per;
  return {
    index,
    tile,
    x: (cell % p.tileCols) * p.width,
    y: Math.floor(cell / p.tileCols) * p.height,
    width: p.width,
    height: p.height,
  };
}

/** The sheet under a time first, then its neighbours within radius —
 * the prefetch order for a hover. */
export function previewTilesAround(p: PreviewIndex, timeS: number, radius: number): number[] {
  const cell = previewCellFor(p, timeS);
  if (!cell) return [];
  const last = Math.floor((p.count - 1) / (p.tileCols * p.tileRows));
  const out = [cell.tile];
  for (let d = 1; d <= radius; d++) {
    if (cell.tile - d >= 0) out.push(cell.tile - d);
    if (cell.tile + d <= last) out.push(cell.tile + d);
  }
  return out;
}

/** The on-screen box for a thumbnail capped at maxW, never upscaled. */
export function previewBoxSize(p: PreviewIndex, maxW: number): { w: number; h: number; scale: number } {
  const scale = Math.min(1, maxW / p.width);
  return { w: Math.round(p.width * scale), h: Math.round(p.height * scale), scale };
}

// ---- chapters ----

/** The chapter containing a time (start inclusive, end exclusive; a last
 * chapter with endMs ≤ 0 is open-ended). */
export function chapterAt(chapters: Chapter[], timeS: number): Chapter | null {
  const ms = timeS * 1000;
  for (let i = chapters.length - 1; i >= 0; i--) {
    const c = chapters[i]!;
    if (ms < c.startMs) continue;
    const open = c.endMs <= 0 && i === chapters.length - 1;
    if (open || ms < c.endMs) return c;
    return null;
  }
  return null;
}

export function chapterLabel(c: Chapter): string {
  const t = c.title.trim();
  return t || `Chapter ${c.index + 1}`;
}

// ---- markers ----

/** The marker playback is inside (start ≤ t < end); an intro wins over a
 * credits span that overlaps it. */
export function activeMarkerAt(markers: Marker[], timeS: number): Marker | null {
  const ms = timeS * 1000;
  let hit: Marker | null = null;
  for (const m of markers) {
    if (ms >= m.startMs && ms < m.endMs) {
      if (m.type === "intro") return m;
      hit ??= m;
    }
  }
  return hit;
}

/** Plex lands a couple of seconds before the intro ends, so a viewer sees
 * the show start rather than a cut into it. */
export const SKIP_CUSHION_S = 2;

/** Where a skip lands: an intro just before its end; final credits at the
 * runtime's end (ended → Up Next / exit); non-final credits at their end so
 * a post-credits scene stays reachable. */
export function skipTargetS(m: Marker, durationS: number): number {
  if (m.type === "intro") return Math.max(m.startMs / 1000, m.endMs / 1000 - SKIP_CUSHION_S);
  if (m.final && durationS > 0) return durationS;
  return m.endMs / 1000;
}

/** The earliest credits start — where Up Next should appear. */
export function upNextStartS(markers: Marker[]): number | null {
  let start: number | null = null;
  for (const m of markers) {
    if (m.type !== "credits") continue;
    const s = m.startMs / 1000;
    if (start === null || s < start) start = s;
  }
  return start;
}

export function sameMarker(a: Marker | null, b: Marker | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.type === b.type && a.startMs === b.startMs && a.endMs === b.endMs;
}

export interface SkipPill {
  label: "Skip Intro" | "Skip Credits";
  target: number;
}

/** What the skip pill shows, if anything: nothing outside a marker, nothing
 * once the viewer skipped this marker, nothing while Up Next owns the
 * screen, and no Skip Credits when a next episode exists — Up Next appears
 * at the credits instead. */
export function skipPillFor(a: {
  marker: Marker | null;
  durationS: number;
  hasNext: boolean;
  upNextVisible: boolean;
  skipped: Marker | null;
}): SkipPill | null {
  const { marker } = a;
  if (!marker || a.upNextVisible || sameMarker(marker, a.skipped)) return null;
  if (marker.type === "credits" && a.hasNext) return null;
  return {
    label: marker.type === "intro" ? "Skip Intro" : "Skip Credits",
    target: skipTargetS(marker, a.durationS),
  };
}
